import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ADS_CONFIG, contextualOnly, currentConsent, useConsentPlatform } from '../ads/sdk';

/** This is an opt-out and CMP bridge, NOT a replacement for a certified CMP. */
export function AdPreferences(): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [contextual, setContextual] = useState(!currentConsent().ads);
  useEffect(() => {
    const open = (event: Event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (window.ConstructCanvasConsent?.openPreferences) { useConsentPlatform(); return; }
      const focused = document.activeElement;
      const menuTrigger = focused?.closest('[role="menu"]')?.getAttribute('aria-labelledby');
      returnFocus.current = (menuTrigger ? document.getElementById(menuTrigger) : focused?.shadowRoot?.activeElement || focused) as HTMLElement | null;
      setContextual(!currentConsent().ads);
      dialog.current?.showModal();
    };
    const update = () => setContextual(!currentConsent().ads);
    document.addEventListener('picplayer:ad-preferences', open);
    document.addEventListener('constructcanvas:consent-updated', update);
    return () => {
      document.removeEventListener('picplayer:ad-preferences', open);
      document.removeEventListener('constructcanvas:consent-updated', update);
    };
  }, []);
  return <dialog ref={dialog} className="ad-preferences-dialog" aria-labelledby="ad-preferences-title"
    onClose={() => returnFocus.current?.isConnected && returnFocus.current.focus()}
    onClick={(event) => { if (event.target === event.currentTarget) dialog.current?.close(); }}>
    <div className="ad-preferences-content">
      <div className="ad-preferences-heading"><h2 id="ad-preferences-title">Ad choices</h2>
        <button type="button" className="icon-button" aria-label="Close ad choices" onClick={() => dialog.current?.close()}><X size={18} /></button></div>
      <p>ConstructCanvas uses PicPlayer’s shared advertising manager. Your model, canvas pixels, project name and saved files are not sent to it.</p>
      <p>{ADS_CONFIG.enabled ? 'Ads default to contextual text. Third-party networks are not enabled without network-specific permission from a connected consent platform.' : 'Advertising is disabled for this build.'}</p>
      <p className="ad-consent-status" role="status">{contextual ? 'Contextual-only mode. No consent-dependent networks enabled.' : 'Network permissions have been supplied by your consent platform.'}</p>
      <button type="button" className="primary-compact" onClick={contextualOnly}>Use contextual ads only</button>
      {window.ConstructCanvasConsent && <button type="button" className="consent-platform-button" onClick={() => { dialog.current?.close(); useConsentPlatform(); }}>Manage permissions with your consent platform</button>}
      <p className="preferences-note">This panel can withdraw network permission; it does not collect permission for personalized advertising. Browser privacy signals are respected by the shared SDK.</p>
      <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy & local data</a>
    </div>
  </dialog>;
}
